import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, arrayUnion } from '../lib/auditedFirestore'; // Added doc, updateDoc, arrayUnion
import { subscribeByIds } from '../lib/subscribeByIds';
import { db, messagingReady } from '../lib/firebase';
import { getToken, onMessage } from 'firebase/messaging'; 
import { useAuth } from '../contexts/AuthContext'; // Import this to get current user ID

const NotificationBell = ({ assignedPapers }) => {
    const [messaging, setMessaging] = useState(null);
    useEffect(() => { let active = true; messagingReady.then(value => { if (active) setMessaging(value); }); return () => { active = false; }; }, []);

    const { user } = useAuth(); // Get current logged-in student
    const [notifications, setNotifications] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const dropdownRef = useRef(null);

    // --- EXISTING LOGIC: Close dropdown if clicked outside ---
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // --- EXISTING LOGIC: Fetch Notifications for Dropdown ---
    useEffect(() => {
        setNotifications([]); setUnreadCount(0);
        return subscribeByIds('notifications', 'paperId', assignedPapers, notes => {
            const recent = notes.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)).slice(0, 10);
            setNotifications(recent);
            setUnreadCount(recent.filter(n => (n.createdAt?.toMillis() || 0) > Date.now() - 86400000).length);
        }, () => { setNotifications([]); setUnreadCount(0); });
    }, [assignedPapers]);

    // --- NEW LOGIC: Request Push Notification Permissions & Get Token ---
    useEffect(() => {
        const requestNotificationPermission = async () => {
            if (!user?.uid || !messaging) return;

            try {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                    // Replace with the VAPID key you generated in Phase 1
                    const currentToken = await getToken(messaging, { 
                        vapidKey: 'BKpfmgn-MZFEvkew_SmgCLiqbmX4qzRVAlffWj2v73oCSU2ZyloNpTaQ6oUUTJM63WFpwA3yssHYKZoFW2XjK-M' 
                    });

                    if (currentToken) {
                        // Save token to the student's user document
                        const userRef = doc(db, 'users', user.uid);
                        await updateDoc(userRef, {
                            fcmTokens: arrayUnion(currentToken) // Use arrayUnion so if they log in on Mac AND Windows, both get saved
                        });
                    } else {
                        console.log('No registration token available.');
                    }
                }
            } catch (error) {
                console.error('An error occurred while retrieving token. ', error);
            }
        };

        requestNotificationPermission();

        // Optional: Handle notifications when the app is OPEN and in focus
        // This ensures they get a little popup or update without needing the background worker
        if (!messaging) return;
        const unsubscribeForeground = onMessage(messaging, (payload) => {
            console.log('Message received in foreground: ', payload);
            // You can use a toast library here like react-toastify to show a popup
            // alert(`New Update: ${payload.notification.title}`); 
        });

        return () => {
            if(unsubscribeForeground) unsubscribeForeground();
        };
    }, [user, messaging]);

    const getIcon = (type) => {
        if (type === 'homework') return '📝';
        if (type === 'exam') return '⏱️';
        return '📢';
    };

    return (
        // ... (Your existing JSX remains exactly the same)
        <div className="relative" ref={dropdownRef}>
            <button 
                aria-label="Notifications" aria-expanded={showDropdown}
                onClick={() => setShowDropdown(!showDropdown)} 
                className="relative p-2 bg-white rounded-full shadow-sm hover:bg-gray-100 transition border border-gray-200"
            >
                <span className="text-xl">🔔</span>
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full animate-pulse">
                        {unreadCount}
                    </span>
                )}
            </button>

            {showDropdown && (
                <div className="absolute right-0 mt-3 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-fade-in">
                    <div className="bg-purple-50 px-4 py-3 border-b border-purple-100">
                        <h3 className="font-bold text-purple-800">Notifications</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-6 text-center text-gray-400 text-sm">
                                No new updates.
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {notifications.map(note => (
                                    <div key={note.id} className="p-3 hover:bg-gray-50 transition flex gap-3 items-start">
                                        <div className="bg-gray-100 p-2 rounded-full h-10 w-10 flex items-center justify-center flex-shrink-0">
                                            {getIcon(note.type)}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-800">{note.title}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{note.message}</p>
                                            <p className="text-[10px] text-gray-400 mt-1">
                                                {note.createdAt?.seconds ? new Date(note.createdAt.seconds * 1000).toLocaleString() : 'Just now'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;